import { useQuery } from "@tanstack/react-query";

import {
  IMissingBaseline,
  IPatientBaselineDetail,
  IPatientWithBaselines,
} from "../../../@types/admin/AdminBaseline";
import { baselinesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Patients with at least one recorded baseline. */
export function usePatientsWithBaselines() {
  const { data, isLoading, isError } = useQuery<IPatientWithBaselines[]>({
    queryKey: ["admin-patients-with-baselines"],
    queryFn: async () =>
      ensureOk(await baselinesService.getPatientsWithBaselines()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { patients: data ?? [], isLoading, isError };
}

/**
 * One patient's baselines. Idle until a patient is chosen, matching Retool's
 * modal, which only ran its query once opened.
 */
export function usePatientBaselines(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientBaselineDetail[]>({
    queryKey: ["admin-patient-baselines", patientId],
    queryFn: async () =>
      ensureOk(await baselinesService.getPatientBaselines(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { baselines: data ?? [], isLoading, isError };
}

/**
 * Patients a published workflow needs a baseline from and hasn't got.
 *
 * The expensive one. Kept idle until its tab is opened so landing on the page
 * does not pay for a report the operator may not have come for.
 */
export function useMissingBaselines(enabled = true) {
  const { data, isLoading, isError } = useQuery<IMissingBaseline[]>({
    queryKey: ["admin-missing-baselines"],
    queryFn: async () => ensureOk(await baselinesService.getMissingBaselines()),
    enabled,
    refetchOnMount: "always",
  });

  return { missingBaselines: data ?? [], isLoading, isError };
}
